import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "./lib/api";
import { Home } from "./app/Home";
import { Login } from "./app/Login";
import { SelectLocations } from "./app/SelectLocations";
import { Shell } from "./app/Shell";
import { AskAccessPage } from "./features/admin/AskAccessPage";
import { AuditLogPage, parseAuditSearch } from "./features/admin/AuditLogPage";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { SharedWithMePage } from "./features/dashboard/SharedWithMePage";
import { ListingDetailPage } from "./features/marketplace/ListingDetail";
import { MarketplacePage } from "./features/marketplace/MarketplacePage";
import { SchedulesPage } from "./features/schedules/SchedulesPage";
import { ContactsPage } from "./features/contacts/ContactsPage";
import type { DateZoom } from "./lib/dateZoom";
import type { Drill } from "./lib/drill";
import { parseDashboardSearch, type PageFilter } from "./lib/pageFilters";

function Root() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Reached from sign-in before an MCP client's grant, before there is a session.
  if (path === "/select-locations") return <SelectLocations />;
  // Opened by Chrome to print a PDF report, on a render token: no session, no shell.
  if (path.startsWith("/print/")) return <Outlet />;
  return <Authed />;
}

function Authed() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  if (me.isLoading) return null;
  if (me.error instanceof ApiError && me.error.status === 401) return <Login />;
  if (!me.data) return <p className="p-8 text-bad">Couldn't reach the server.</p>;
  return (
    <Shell me={me.data}>
      <Outlet />
    </Shell>
  );
}

const rootRoute = createRootRoute({ component: Root });
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Home });
const NO_FILTERS: PageFilter[] = [];
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/d/$id",
  // Drill-down filters (`f`), the date zoom (`z`) and a breakdown (`d`) live in the URL, so Back
  // undoes a click and a shared link keeps them.
  validateSearch: parseDashboardSearch,
  component: function DashboardRoute() {
    const { id } = dashboardRoute.useParams();
    const { f, z, d } = dashboardRoute.useSearch();
    const navigate = dashboardRoute.useNavigate();
    const go = (next: { f?: PageFilter[]; z?: DateZoom; d?: Drill }, replace?: boolean) =>
      void navigate({ search: { ...(next.f?.length ? { f: next.f } : {}), ...(next.z ? { z: next.z } : {}), ...(next.d ? { d: next.d } : {}) }, replace });
    return (
      <DashboardPage
        key={id} id={id} pageFilters={f ?? NO_FILTERS} zoom={z} drill={d}
        onPageFiltersChange={(next) => go({ f: next, z, d })}
        onZoomChange={(next) => go({ f, z: next, d })}
        onDrillChange={(next, opts) => go({ f, z, d: next }, opts?.replace)}
      />
    );
  },
});

const marketplaceRoute = createRoute({ getParentRoute: () => rootRoute, path: "/marketplace", component: MarketplacePage });
const listingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketplace/$id",
  component: function ListingRoute() {
    const { id } = listingRoute.useParams();
    return <ListingDetailPage key={id} id={id} />;
  },
});

const sharedRoute = createRoute({ getParentRoute: () => rootRoute, path: "/shared", component: SharedWithMePage });
const schedulesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/schedules", component: SchedulesPage });
const contactsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/contacts", component: ContactsPage });
// The PDF feature's print page (features/pdf), loaded only when printing.
const printRoute = createRoute({ getParentRoute: () => rootRoute, path: "/print/d/$id", component: lazyRouteComponent(() => import("./features/pdf/PrintPage"), "PrintRoute") });
const auditRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/audit", validateSearch: parseAuditSearch, component: AuditLogPage });
const askAccessRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin/ask", component: AskAccessPage });

export const router = createRouter({
  routeTree: rootRoute.addChildren([indexRoute, dashboardRoute, marketplaceRoute, listingRoute, sharedRoute, schedulesRoute, contactsRoute, auditRoute, askAccessRoute, printRoute]),
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
